import { useState, useEffect, useRef } from 'react';
import { isAuthenticatedSync, clearAuthToken, setAuthState, refreshAccessToken } from '@/lib/onshape-auth';

/**
 * Build fetch options for /api/auth/verify.
 * Includes Bearer token from sessionStorage when available (fallback for iframe/third-party cookie blocking).
 */
function getVerifyFetchOptions() {
  const opts = { method: 'GET', credentials: 'include' };
  try {
    const token = sessionStorage.getItem('fastenai_access_token');
    if (token) {
      opts.headers = { Authorization: `Bearer ${token}` };
    }
  } catch (_) { /* sessionStorage unavailable */ }
  return opts;
}

/**
 * Authentication hook
 * Uses cookie-based authentication (HttpOnly cookie set by Cloud Function)
 * Falls back to Bearer token when cookies are blocked (e.g. in Onshape iframe)
 * @returns {Object} Auth state and methods
 */
export function useAuth() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const oauthSuccessReceivedRef = useRef(false); // Track if OAuth success message was received

  useEffect(() => {
    const checkAuth = async () => {
      let hadStoredUserInfo = false;
      // First, check sessionStorage for userInfo (persisted from OAuth callback)
      const storedUserInfo = sessionStorage.getItem('fastenai_user_info');
      if (storedUserInfo) {
        try {
          const parsedUserInfo = JSON.parse(storedUserInfo);
          setUserInfo(parsedUserInfo);
          setAuthenticated(true);
          await setAuthState(true);
          hadStoredUserInfo = true;
          // Don't setLoading(false) yet — wait for /api/auth/verify to store the
          // access token so downstream effects (e.g. elementType detection) have it.
          // Fall through to /api/auth/verify so usage counters are always fresh.
        } catch (e) {
          console.error('Error parsing stored user info:', e);
          sessionStorage.removeItem('fastenai_user_info');
        }
      }
      
      // If we already have userInfo from OAuth message, don't overwrite it
      if (oauthSuccessReceivedRef.current && userInfo) {
        setLoading(false);
        return;
      }
      
      // Check for auth=success query parameter (set when redirecting from website after auth)
      const urlParams = new URLSearchParams(window.location.search);
      const authSuccess = urlParams.get('auth') === 'success';
      
      if (authSuccess) {
        // Set localStorage flag since cookie is already set
        await setAuthState(true);
        setAuthenticated(true);
        
        // First, try to get user info from sessionStorage (set by callback)
        const storedUserInfo = sessionStorage.getItem('fastenai_user_info');
        if (storedUserInfo) {
          try {
            const userInfo = JSON.parse(storedUserInfo);
            setUserInfo(userInfo);
            sessionStorage.removeItem('fastenai_user_info'); // Clean up
          } catch (e) {
            console.error('Error parsing stored user info:', e);
          }
        }
        
        // Also try to fetch from API (cookie should be set by now)
        const appUrl = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
        try {
          const response = await fetch(`${appUrl}/api/auth/verify`, getVerifyFetchOptions());
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.user) {
              setUserInfo(result.user);
              // Store access token for Authorization header
              if (result.access_token) {
                sessionStorage.setItem('fastenai_access_token', result.access_token);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user info after auth success:', error);
        }
        setLoading(false);
        // Clean up URL by removing the query parameter
        const newUrl = window.location.pathname + (window.location.hash || '');
        window.history.replaceState({}, '', newUrl);
        return;
      }

      // PRIORITY: Verify cookie first (source of truth)
      // Cookie persists across sessions, so this is the most reliable check
      // Include Bearer token from sessionStorage when available (fallback for iframe cookie blocking)
      const appUrl = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
      try {
        const response = await fetch(`${appUrl}/api/auth/verify`, getVerifyFetchOptions());
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.success) {
            // Cookie is valid - this is the source of truth
            // Update localStorage flag to match cookie state
            await setAuthState(true);
            setAuthenticated(true);
            // Store user info and access token
            if (result.user) {
              setUserInfo(result.user);
            }
            if (result.access_token) {
              sessionStorage.setItem('fastenai_access_token', result.access_token);
            }
            setLoading(false);
            return;
          } else {
            // Cookie is invalid, but don't clear if we already loaded userInfo from sessionStorage
            if (!hadStoredUserInfo) {
              await setAuthState(false);
              setAuthenticated(false);
              setUserInfo(null);
            } else {
              setAuthenticated(true);
            }
            setLoading(false);
            return;
          }
        } else {
          // Non-ok response (e.g. 401 expired token) — try silent token refresh
          try {
            const refreshResult = await refreshAccessToken();
            if (refreshResult.success && refreshResult.access_token) {
              // Retry verifyAuth with the new token now in sessionStorage
              const retryResponse = await fetch(`${appUrl}/api/auth/verify`, getVerifyFetchOptions());
              if (retryResponse.ok) {
                const retryResult = await retryResponse.json();
                if (retryResult.success) {
                  await setAuthState(true);
                  setAuthenticated(true);
                  if (retryResult.user) setUserInfo(retryResult.user);
                  if (retryResult.access_token) {
                    sessionStorage.setItem('fastenai_access_token', retryResult.access_token);
                  }
                  setLoading(false);
                  return;
                }
              }
            }
          } catch (refreshError) {
            console.warn('[useAuth] Token refresh failed:', refreshError.message);
          }
          // No valid cookie found - but don't clear if we already loaded userInfo from sessionStorage
          if (!hadStoredUserInfo) {
            await setAuthState(false);
            setAuthenticated(false);
            setUserInfo(null);
          } else {
            setAuthenticated(true);
          }
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error verifying auth:', error);
        // On error, check localStorage as fallback (for offline/network issues)
        // But don't trust it fully - cookie verification is still needed
        const hasLocalFlag = isAuthenticatedSync();
        if (hasLocalFlag) {
          // Storage flag exists but cookie verification failed
          // This might be a network issue, so we'll show as authenticated
          // but the next API call will verify the cookie
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          setUserInfo(null);
        }
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth messages from pop-up
    const handleMessage = (event) => {
      // Verify origin for security
      const appUrl = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
      
      if (event.origin !== appUrl) {
        return;
      }

      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'ONSHAPE_AUTH_SUCCESS') {
        // Mark that we've received OAuth success
        oauthSuccessReceivedRef.current = true;
        // Auth flag is set by exchangeCodeForToken function
        // Cookie is automatically set by Cloud Function
        setAuthenticated(true);
        
        // Store access token if provided in message
        if (event.data.access_token) {
          try {
            sessionStorage.setItem('fastenai_access_token', event.data.access_token);
            console.log('[useAuth] Token stored from OAuth message, length:', event.data.access_token.length);
          } catch (error) {
            console.error('[useAuth] Failed to store token from message:', error);
          }
        }
        
        // Use user data directly from the message (callback already has it)
        if (event.data.user) {
          setUserInfo(event.data.user);
          // Store in sessionStorage for persistence
          sessionStorage.setItem('fastenai_user_info', JSON.stringify(event.data.user));
        }
        
        // Always fetch from /api/auth/verify to ensure we have the latest token and user info
        // This also works as a fallback if token wasn't in the message
        const appUrl = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
        fetch(`${appUrl}/api/auth/verify`, getVerifyFetchOptions())
          .then(response => {
            if (response.ok) {
              return response.json();
            }
            throw new Error('Failed to verify auth');
          })
          .then(result => {
            if (result.success) {
              if (result.user) {
                setUserInfo(result.user);
              }
              // Store access token from verify endpoint (most reliable source)
              if (result.access_token) {
                try {
                  sessionStorage.setItem('fastenai_access_token', result.access_token);
                  console.log('[useAuth] Token stored from /api/auth/verify, length:', result.access_token.length);
                } catch (error) {
                  console.error('[useAuth] Failed to store token from verify:', error);
                }
              }
            }
          })
          .catch(error => {
            console.error('[useAuth] Error fetching user info in message handler:', error);
          });
      } else if (event.data.type === 'ONSHAPE_AUTH_ERROR') {
        console.error('Authentication error:', event.data.error);
        setAuthenticated(false);
        setUserInfo(null);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const refreshUserInfo = async () => {
    const appUrl = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
    try {
      const response = await fetch(`${appUrl}/api/auth/verify`, getVerifyFetchOptions());
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.user) {
          setUserInfo(result.user);
        }
      }
    } catch (error) {
      console.error('[useAuth] refreshUserInfo failed:', error);
    }
  };

  const logout = async () => {
    await clearAuthToken();
    setAuthenticated(false);
    setUserInfo(null);
    // TODO: Call backend logout endpoint to clear HttpOnly cookie
    // For now, just clear frontend flag
  };

  return {
    authenticated,
    loading,
    userInfo,
    // Token is in HttpOnly cookie, not accessible via JavaScript
    // API calls will automatically include the cookie
    refreshUserInfo,
    logout,
  };
}
